/**
 * @file booking.store.ts
 * @path apps/website/src/features/booking/store/booking.store.ts
 * @description
 * Single Zustand store replacing BookingContext (useReducer) + PriceContext +
 * ValidationContext. All business logic lives here; components just select slices.
 */

import {
  Activity,
  BookingState,
  CompletedBooking,
  Passenger,
  RouteStop,
  SelectedVehicle,
  Service,
} from "@sirbastians/types";
import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { services } from "@/data/services";
import { activitiesData } from "@/data/activities";
import { getTotalPrice, PriceBreakdown } from "./pricing.util";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const emptyBreakdown = (): PriceBreakdown => ({
  servicePrice: 0,
  activitiesPrice: 0,
  addOnsPrice: 0,
  extraStopsPrice: 0,
  vehiclesPrice: 0,
  total: 0,
});

const initialDraft: Omit<
  BookingState,
  "completedBookings" | "editingBookingId"
> = {
  selectedService: null,
  includedActivities: [],
  selectedActivities: [],
  passengers: [],
  luggageCount: 0,
  babySeats: 0,
  trailerNeeded: false,
  flightNumber: "",
  selectedDate: undefined,
  selectedTime: null,
  duration: null,
  selectedVehicles: [],
  routeStops: [],
};

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

export interface BookingStore extends BookingState {
  // --- Computed (cached on every state change) ---
  pricing: PriceBreakdown;
  adultsCount: number;
  childrenCount: number;
  infantsCount: number;
  primaryPassenger: Passenger | undefined;

  // --- Activity booking flag ---
  cameFromActivity: boolean;
  setCameFromActivity: (val: boolean) => void;

  // --- Validation UI state ---
  showValidation: boolean;
  triggerValidation: () => void;
  clearValidation: () => void;

  // --- Step 1: Service ---
  setService: (service: Service) => void;

  // --- Step 2: Trip Details ---
  setPassengers: (passengers: Passenger[]) => void;
  updatePassenger: (passenger: Passenger) => void;
  updatePassengerCount: (
    type: "adult" | "child" | "infant",
    newCount: number,
  ) => void;
  updateBabySeatCount: (count: number) => void;
  updatePrimaryContact: (field: keyof Passenger, value: string) => void;
  handleVehicleChange: (
    vehicleId: string,
    type: "standard" | "vip",
    delta: number,
  ) => void;
  setTrailerNeeded: (val: boolean) => void;
  setFlightNumber: (val: string) => void;
  setDuration: (val: "half-day" | "full-day" | null) => void;
  setLuggageCount: (count: number) => void;

  // --- Step 3: Activities ---
  toggleActivity: (activity: Activity) => void;
  setIncludedActivities: (activities: Activity[]) => void;

  // --- Step 4: Schedule & Route ---
  setDate: (date: Date | undefined) => void;
  setTime: (time: string | null) => void;
  setRouteStops: (stops: RouteStop[]) => void;
  updateStopAddress: (id: string, address: string) => void;
  addStop: () => void;
  removeStop: (id: string) => void;
  moveStop: (index: number, direction: "up" | "down") => void;

  // --- Step 5 / Cart ---
  saveCurrentBooking: () => void;
  loadForEdit: (id: string) => void;
  deleteBooking: (id: string) => void;
  resetForNewBooking: () => void;
  addReturnTrip: () => void;

  // --- Internal ---
  _recalcPricing: () => void;
  _checkVehicleCapacity: (totalSeats: number) => void;
}

// ---------------------------------------------------------------------------
// Internal: derive computed passenger counts from array
// ---------------------------------------------------------------------------

const derivePassengerCounts = (passengers: Passenger[]) => ({
  adultsCount: passengers.filter((p) => p.type === "adult").length,
  childrenCount: passengers.filter((p) => p.type === "child").length,
  infantsCount: passengers.filter((p) => p.type === "infant").length,
  primaryPassenger: passengers.find((p) => p.isPrimary),
});

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useBookingStore = create<BookingStore>((set, get) => ({
  // ── Initial State ──────────────────────────────────────────────────────────
  ...initialDraft,
  completedBookings: [],
  editingBookingId: null,

  pricing: emptyBreakdown(),
  adultsCount: 0,
  childrenCount: 0,
  infantsCount: 0,
  primaryPassenger: undefined,

  showValidation: false,
  cameFromActivity: false,

  // ── Validation UI ─────────────────────────────────────────────────────────

  triggerValidation: () => set({ showValidation: true }),
  clearValidation: () => set({ showValidation: false }),
  setCameFromActivity: (val) => set({ cameFromActivity: val }),

  // ── Internal helpers ──────────────────────────────────────────────────────

  _recalcPricing: () => {
    const s = get();
    const pricing = getTotalPrice({
      service: s.selectedService,
      includedActivities: s.includedActivities,
      selectedActivities: s.selectedActivities,
      adultsCount: s.adultsCount,
      childrenCount: s.childrenCount,
      infantsCount: s.infantsCount,
      babySeats: s.babySeats,
      trailerNeeded: s.trailerNeeded,
      routeStops: s.routeStops,
      selectedVehicles: s.selectedVehicles,
      duration: s.duration,
    });
    set({ pricing });
  },

  _checkVehicleCapacity: (totalSeats: number) => {
    const { selectedService } = get();
    if (!selectedService?.standardVehicle) return;
    const capacity = selectedService.standardVehicle.maxPassengers;
    const countToSet = Math.max(1, Math.ceil(totalSeats / capacity));
    const newVehicles: SelectedVehicle[] = [
      {
        id: selectedService.standardVehicle.id,
        type: "standard",
        count: countToSet,
      },
    ];
    set({ selectedVehicles: newVehicles });
    get()._recalcPricing();
  },

  // ── Step 1: Service ───────────────────────────────────────────────────────

  setService: (service) => {
    const stops: RouteStop[] = (service.route?.stops || []).map((stop) => ({
      id: uuidv4(),
      type: (stop.id.includes("pickup")
        ? "pickup"
        : stop.id.includes("dropoff")
          ? "dropoff"
          : "stop") as RouteStop["type"],
      name: stop.name,
      address: stop.address ?? "",
      isEditable: stop.isEditable,
      isExtraStop: false,
    }));

    const minAdults = service.packageConfig?.passengerConfig?.minAdults ?? 1;
    const initialPassengers: Passenger[] = Array.from(
      { length: minAdults },
      (_, i) => ({
        id: uuidv4(),
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        type: "adult" as const,
        isPrimary: i === 0,
      }),
    );
    const counts = derivePassengerCounts(initialPassengers);

    const initialVehicles: SelectedVehicle[] = [];
    if (service.standardVehicle) {
      const capacity = service.standardVehicle.maxPassengers;
      const vehicleCount = Math.max(1, Math.ceil(minAdults / capacity));
      initialVehicles.push({
        id: service.standardVehicle.id,
        type: "standard",
        count: vehicleCount,
      });
    }

    set({
      selectedService: service,
      includedActivities: service.includedActivities ?? [],
      selectedActivities: [],
      selectedVehicles: initialVehicles,
      routeStops: stops,
      passengers: initialPassengers,
      ...counts,
      duration: service.slug !== "private-vehicle-hire" ? null : get().duration,
      trailerNeeded: false,
      showValidation: false,
    });

    get()._recalcPricing();
  },

  // ── Step 2: Trip Details ──────────────────────────────────────────────────

  setPassengers: (passengers) => {
    set({ passengers, ...derivePassengerCounts(passengers) });
    get()._recalcPricing();
  },

  updatePassenger: (passenger) => {
    const passengers = get().passengers.map((p) =>
      p.id === passenger.id ? passenger : p,
    );
    set({ passengers, ...derivePassengerCounts(passengers) });
  },

  updatePassengerCount: (type, newCount) => {
    const { passengers, adultsCount, childrenCount, babySeats } = get();
    const currentCount = passengers.filter((p) => p.type === type).length;

    let newPassengers = [...passengers];

    if (newCount > currentCount) {
      const toAdd: Passenger[] = Array.from(
        { length: newCount - currentCount },
        () => ({
          id: uuidv4(),
          firstName: "",
          lastName: "",
          type,
          isPrimary: false,
        }),
      );
      newPassengers = [...newPassengers, ...toAdd];
    } else if (newCount < currentCount) {
      let removed = 0;
      const diff = currentCount - newCount;
      newPassengers = newPassengers.filter((p) => {
        if (removed < diff && p.type === type && !p.isPrimary) {
          removed++;
          return false;
        }
        return true;
      });
    }

    const counts = derivePassengerCounts(newPassengers);
    set({ passengers: newPassengers, ...counts });

    const nextAdults = type === "adult" ? newCount : adultsCount;
    const nextChildren = type === "child" ? newCount : childrenCount;
    get()._checkVehicleCapacity(nextAdults + nextChildren + babySeats);
  },

  updateBabySeatCount: (newCount) => {
    const { adultsCount, childrenCount } = get();
    set({ babySeats: newCount });
    get()._checkVehicleCapacity(adultsCount + childrenCount + newCount);
  },

  updatePrimaryContact: (field, value) => {
    const primary = get().passengers.find((p) => p.isPrimary);
    if (!primary) return;
    get().updatePassenger({ ...primary, [field]: value });
  },

  handleVehicleChange: (vehicleId, type, delta) => {
    const current = get().selectedVehicles;
    const existing = current.find((v) => v.id === vehicleId);
    const currentCount = existing?.count ?? 0;
    const newCount = Math.max(0, currentCount + delta);

    let newVehicles: SelectedVehicle[];
    if (newCount === 0) {
      newVehicles = current.filter((v) => v.id !== vehicleId);
    } else if (existing) {
      newVehicles = current.map((v) =>
        v.id === vehicleId ? { ...v, count: newCount } : v,
      );
    } else {
      newVehicles = [...current, { id: vehicleId, type, count: newCount }];
    }

    set({ selectedVehicles: newVehicles });
    get()._recalcPricing();
  },

  setTrailerNeeded: (val) => {
    set({ trailerNeeded: val });
    get()._recalcPricing();
  },

  setFlightNumber: (val) => set({ flightNumber: val }),

  setDuration: (val) => {
    set({ duration: val });
    get()._recalcPricing();
  },

  setLuggageCount: (count) => set({ luggageCount: count }),

  // ── Step 3: Activities ────────────────────────────────────────────────────

  toggleActivity: (activity) => {
    const current = get().selectedActivities;
    const isSelected = current.some((a) => a.id === activity.id);
    const selectedActivities = isSelected
      ? current.filter((a) => a.id !== activity.id)
      : [...current, activity];
    set({ selectedActivities });
    get()._recalcPricing();
  },

  setIncludedActivities: (activities) => {
    set({ includedActivities: activities });
    get()._recalcPricing();
  },

  // ── Step 4: Schedule & Route ──────────────────────────────────────────────

  setDate: (date) => {
    set({ selectedDate: date });
    if (!date) set({ selectedTime: null });
  },

  setTime: (time) => set({ selectedTime: time }),

  setRouteStops: (stops) => {
    set({ routeStops: stops });
    get()._recalcPricing();
  },

  updateStopAddress: (id, address) => {
    const stops = get().routeStops.map((s) =>
      s.id === id ? { ...s, address } : s,
    );
    set({ routeStops: stops });
  },

  addStop: () => {
    const stops = get().routeStops;
    const newStop: RouteStop = {
      id: uuidv4(),
      type: "stop",
      name: "Additional Stop",
      address: "",
      isEditable: true,
      isExtraStop: true,
    };
    const dropoffIndex = stops.findIndex((s) => s.type === "dropoff");
    const insertAt = dropoffIndex === -1 ? stops.length : dropoffIndex;
    const next = [...stops];
    next.splice(insertAt, 0, newStop);
    set({ routeStops: next });
    get()._recalcPricing();
  },

  removeStop: (id) => {
    const stops = get()
      .routeStops.filter((s) => s.id !== id)
      .map((stop, idx, arr) => ({
        ...stop,
        type: (idx === 0
          ? "pickup"
          : idx === arr.length - 1
            ? "dropoff"
            : "stop") as RouteStop["type"],
      }));
    set({ routeStops: stops });
    get()._recalcPricing();
  },

  moveStop: (index, direction) => {
    const stops = [...get().routeStops];
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === stops.length - 1) return;
    const target = direction === "up" ? index - 1 : index + 1;
    [stops[index], stops[target]] = [stops[target], stops[index]];
    const updated = stops.map((s, i, arr) => ({
      ...s,
      type: (i === 0
        ? "pickup"
        : i === arr.length - 1
          ? "dropoff"
          : "stop") as RouteStop["type"],
    }));
    set({ routeStops: updated });
  },

  // ── Step 5 / Cart ─────────────────────────────────────────────────────────

  saveCurrentBooking: () => {
    const s = get();
    if (!s.selectedService || !s.selectedDate || !s.selectedTime) {
      console.error("Cannot save booking: missing service, date, or time");
      return;
    }

    const newBooking: CompletedBooking = {
      id: s.editingBookingId || uuidv4(),
      bookingDate: s.selectedDate,
      selectedService: s.selectedService,
      includedActivities: s.includedActivities,
      selectedActivities: s.selectedActivities,
      passengers: s.passengers,
      adultCount: s.adultsCount,
      childCount: s.childrenCount,
      infantCount: s.infantsCount,
      luggageCount: s.luggageCount,
      babySeats: s.babySeats,
      selectedTime: s.selectedTime,
      duration: s.duration,
      selectedVehicles: s.selectedVehicles,
      routeStops: s.routeStops,
      trailerNeeded: s.trailerNeeded,
      flightNumber: s.flightNumber,
      pricing: {
        service: s.pricing.servicePrice,
        activities: s.pricing.activitiesPrice,
        addOns:
          s.pricing.addOnsPrice +
          s.pricing.vehiclesPrice +
          s.pricing.extraStopsPrice,
        total: s.pricing.total,
      },
    };

    set({
      ...initialDraft,
      completedBookings: [...s.completedBookings, newBooking],
      editingBookingId: null,
      pricing: emptyBreakdown(),
      adultsCount: 0,
      childrenCount: 0,
      infantsCount: 0,
      primaryPassenger: undefined,
    });
  },

  loadForEdit: (id) => {
    const { completedBookings } = get();
    const booking = completedBookings.find((b) => b.id === id);
    if (!booking) return;

    const remaining = completedBookings.filter((b) => b.id !== id);
    const counts = derivePassengerCounts(booking.passengers);

    set({
      selectedService: booking.selectedService,
      includedActivities: booking.includedActivities,
      selectedActivities: booking.selectedActivities,
      passengers: booking.passengers,
      ...counts,
      luggageCount: booking.luggageCount ?? 0,
      babySeats: booking.babySeats ?? 0,
      trailerNeeded: booking.trailerNeeded,
      flightNumber: booking.flightNumber ?? "",
      selectedDate: booking.bookingDate,
      selectedTime: booking.selectedTime,
      duration: booking.duration,
      selectedVehicles: booking.selectedVehicles,
      routeStops: booking.routeStops,
      editingBookingId: booking.id,
      completedBookings: remaining,
    });

    get()._recalcPricing();
  },

  deleteBooking: (id) => {
    set({
      completedBookings: get().completedBookings.filter((b) => b.id !== id),
    });
  },

  resetForNewBooking: () => {
    set({
      ...initialDraft,
      completedBookings: get().completedBookings,
      editingBookingId: null,
      pricing: emptyBreakdown(),
      adultsCount: 0,
      childrenCount: 0,
      infantsCount: 0,
      primaryPassenger: undefined,
      showValidation: false,
      cameFromActivity: false,
    });
  },

  addReturnTrip: () => {
    const { completedBookings, selectedService, selectedDate, selectedTime } =
      get();

    if (selectedService && selectedDate && selectedTime) {
      get().saveCurrentBooking();
    }

    const lastBooking =
      get().completedBookings[get().completedBookings.length - 1];
    if (!lastBooking) return;

    const reversedStops: RouteStop[] = [...lastBooking.routeStops]
      .reverse()
      .map((stop, idx, arr) => ({
        ...stop,
        id: uuidv4(),
        type: (idx === 0
          ? "pickup"
          : idx === arr.length - 1
            ? "dropoff"
            : "stop") as RouteStop["type"],
      }));

    const passengersCopy = lastBooking.passengers.map((p) => ({ ...p }));
    const counts = derivePassengerCounts(passengersCopy);

    set({
      selectedService: lastBooking.selectedService,
      includedActivities: lastBooking.includedActivities,
      selectedActivities: [],
      passengers: passengersCopy,
      ...counts,
      luggageCount: lastBooking.luggageCount ?? 0,
      babySeats: lastBooking.babySeats ?? 0,
      trailerNeeded: lastBooking.trailerNeeded,
      flightNumber: "",
      selectedDate: undefined,
      selectedTime: null,
      duration: lastBooking.duration,
      selectedVehicles: lastBooking.selectedVehicles,
      routeStops: reversedStops,
      editingBookingId: null,
      showValidation: false,
    });

    get()._recalcPricing();
  },
}));

// ---------------------------------------------------------------------------
// URL Initialisation — call once on app mount
// ---------------------------------------------------------------------------

export const initStoreFromUrl = (slug: string) => {
  const {
    setService,
    setCameFromActivity,
    toggleActivity,
    selectedActivities,
  } = useBookingStore.getState();

  const serviceMatch = services.find((s: Service) => s.slug === slug);
  if (serviceMatch) {
    setService(serviceMatch);
    return;
  }

  const activityMatch = activitiesData.find((a) => a.slug === slug);
  if (activityMatch) {
    const pvh = services.find(
      (s: Service) => s.slug === "private-vehicle-hire",
    );
    if (pvh) {
      setService(pvh);
      setCameFromActivity(true);

      const alreadySelected = selectedActivities.some(
        (a) => a.id === activityMatch.id,
      );
      if (!alreadySelected) {
        toggleActivity(activityMatch);
      }
    }
  }
};

// ---------------------------------------------------------------------------
// Convenience selectors
// ---------------------------------------------------------------------------

export const selectPricing = (s: BookingStore) => s.pricing;
export const selectCompletedBookings = (s: BookingStore) => s.completedBookings;
export const selectActiveService = (s: BookingStore) => s.selectedService;

export const selectShowReturnTrip = (s: BookingStore): boolean => {
  const last = s.completedBookings[s.completedBookings.length - 1];
  const transferCount = s.completedBookings.filter(
    (b) => b.selectedService.slug === "airport-transfers",
  ).length;
  return (
    !!last &&
    last.selectedService.slug === "airport-transfers" &&
    transferCount < 2
  );
};

export const selectIsItineraryLocked = (s: BookingStore): boolean => {
  const transferCount = s.completedBookings.filter(
    (b) => b.selectedService.slug === "airport-transfers",
  ).length;
  return transferCount >= 2;
};
